import MarkdownIt from 'markdown-it';
import { AssistantError } from './assistant';

export type MessageTextFormat = 'plain' | 'whatsapp';
export type MessageLinkPolicy = (url: URL) => boolean;

function parser(
  allowLink: MessageLinkPolicy = () => true,
  locale: 'es-PE' | 'en-US' = 'es-PE'
) {
  const markdown = new MarkdownIt({ html: false, breaks: true, linkify: true });
  markdown.disable(['image', 'table']);
  const validate = markdown.validateLink.bind(markdown);
  markdown.validateLink = (value) => {
    const url = URL.parse(value);
    return (
      !!url &&
      ['https:', 'http:'].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      validate(value) &&
      allowLink(url)
    );
  };
  markdown.renderer.rules.link_open = (
    tokens,
    index,
    options,
    _env,
    renderer
  ) => {
    const link = tokens[index]!;
    link.attrSet('rel', 'noopener noreferrer');
    link.attrSet('target', '_blank');
    const href = link.attrGet('href');
    const label = tokens[index + 1];
    if (
      typeof href === 'string' &&
      URL.parse(href)?.searchParams.has('selection') &&
      label?.type === 'text' &&
      tokens[index + 2]?.type === 'link_close' &&
      (label.content === href ||
        label.content === markdown.normalizeLinkText(href))
    )
      label.content =
        locale === 'es-PE' ? 'Ver departamentos' : 'View apartments';
    return renderer.renderToken(tokens, index, options);
  };
  return markdown;
}

function bounded(text: string): string {
  if (text.length > 32_000)
    throw new AssistantError('MESSAGE_FORMAT_LIMIT_EXCEEDED', 413);
  return text;
}

/** Only parser-generated HTML is returned; source HTML and images are never enabled. */
export function renderMessageHtml(
  text: string,
  allowLink?: MessageLinkPolicy,
  locale?: 'es-PE' | 'en-US'
): string {
  return parser(allowLink, locale).render(bounded(text));
}

export function renderMessageText(
  text: string,
  format: MessageTextFormat = 'plain'
): string {
  const markdown = parser();
  const tokens = markdown.parse(bounded(text), {});
  const lists: { ordered: boolean; next: number }[] = [];
  let output = '';
  for (const token of tokens) {
    if (
      token.type === 'bullet_list_open' ||
      token.type === 'ordered_list_open'
    ) {
      lists.push({
        ordered: token.type === 'ordered_list_open',
        next: Number(token.attrGet('start') ?? 1),
      });
    } else if (
      token.type === 'bullet_list_close' ||
      token.type === 'ordered_list_close'
    ) {
      lists.pop();
    } else if (token.type === 'list_item_open') {
      const list = lists.at(-1)!;
      output += `${'  '.repeat(lists.length - 1)}${list.ordered ? `${list.next++}.` : '-'} `;
    } else if (token.type === 'inline') {
      const links: { url: string; start: number }[] = [];
      for (const child of token.children ?? []) {
        if (child.type === 'text' || child.type === 'code_inline')
          output += child.content;
        else if (child.type === 'softbreak' || child.type === 'hardbreak')
          output += '\n';
        else if (child.type === 'link_open')
          links.push({
            url: String(child.attrGet('href') ?? ''),
            start: output.length,
          });
        else if (child.type === 'link_close') {
          const link = links.pop()!;
          const label = output.slice(link.start);
          if (label === markdown.normalizeLinkText(link.url))
            output = output.slice(0, link.start) + link.url;
          else if (label !== link.url) output += ` (${link.url})`;
        } else if (
          format === 'whatsapp' &&
          /^(strong|em|s)_(open|close)$/u.test(child.type)
        ) {
          output += child.type.startsWith('strong')
            ? '*'
            : child.type.startsWith('em')
              ? '_'
              : '~';
        }
      }
    } else if (token.type === 'fence' || token.type === 'code_block')
      output += `${token.content}\n`;
    else if (token.type === 'paragraph_close' || token.type === 'heading_close')
      output += lists.length ? '\n' : '\n\n';
    else if (token.type === 'hr') output += '\n';
  }
  return output.trim();
}
